import { create } from 'zustand'
import { tracks, playerState, remotePlayers } from '../api/client'
import type { PlayerStateData, RemotePlayer } from '../api/client'
import { useSettingsStore } from './settings'
import { useVideoPlayerStore } from './videoPlayer'
import type { TrackWithDetails, Track } from '../types'

// Helper to check if a track should be skipped (is downvoted)
async function shouldSkipTrack(trackId: string): Promise<boolean> {
  const { skipDownvoted } = useSettingsStore.getState()
  if (!skipDownvoted) return false

  try {
    const { liked } = await tracks.getLikeStatus(trackId)
    return liked === false // Skip if explicitly disliked
  } catch {
    return false // Don't skip on error
  }
}

// Load persisted volume from localStorage
function getPersistedVolume(): number {
  try {
    const stored = localStorage.getItem('s3nd-volume')
    if (stored) {
      const vol = parseFloat(stored)
      if (!isNaN(vol) && vol >= 0 && vol <= 1) return vol
    }
  } catch {}
  return 0.8 // Default volume
}

// Global audio element for playback
let audioElement: HTMLAudioElement | null = null
let animationFrameId: number | null = null

function getAudioElement(): HTMLAudioElement {
  if (!audioElement) {
    audioElement = new Audio()
    audioElement.preload = 'auto'
  }
  return audioElement
}

// Debounce timer for state persistence
let saveStateTimer: ReturnType<typeof setTimeout> | null = null
let isRestoring = false // Flag to prevent saving during restore

interface PlayerState {
  // Current track
  currentTrack: TrackWithDetails | Track | null
  isPlaying: boolean
  progress: number // 0-100
  duration: number // in seconds
  volume: number // 0-1
  isMuted: boolean
  currentTime: number // in seconds, for lyrics sync

  // Queue
  queue: (TrackWithDetails | Track)[]
  queueIndex: number
  originalQueue: (TrackWithDetails | Track)[] // Pre-shuffle order

  // Playback modes
  shuffleEnabled: boolean
  repeatMode: 'off' | 'one' | 'all'

  // State restoration
  stateLoaded: boolean

  // Remote output
  remotePlayerId: string | null
  remoteStatus: RemotePlayer | null

  // Actions
  setRemotePlayer: (player: RemotePlayer | null) => void
  refreshRemoteStatus: () => Promise<void>
  play: (track: TrackWithDetails | Track) => Promise<void>
  playQueue: (tracks: (TrackWithDetails | Track)[], startIndex?: number) => Promise<void>
  pause: () => void
  resume: () => void
  next: () => Promise<void>
  previous: () => void
  seek: (percent: number) => void
  setVolume: (volume: number) => void
  toggleMute: () => void
  addToQueue: (track: TrackWithDetails | Track) => void
  removeFromQueue: (index: number) => void
  reorderQueue: (fromIndex: number, toIndex: number) => void
  playFromQueue: (index: number) => void
  clearQueue: () => void
  toggleShuffle: () => void
  cycleRepeatMode: () => void
  restoreState: () => Promise<void>
}

export const usePlayerStore = create<PlayerState>((set, get) => ({
  currentTrack: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  volume: getPersistedVolume(),
  isMuted: false,
  currentTime: 0,
  queue: [],
  queueIndex: 0,
  originalQueue: [],
  shuffleEnabled: false,
  repeatMode: 'off',
  stateLoaded: false,
  remotePlayerId: localStorage.getItem('s3nd-remote-player-id'),
  remoteStatus: null,

  setRemotePlayer: (player) => {
    if (player) {
      // Selecting a Pi makes it the active output. Stop local browser audio so
      // the next play action does not appear to keep playing only here.
      if (audioElement) {
        audioElement.pause()
      }
      localStorage.setItem('s3nd-remote-player-id', player.player_id)
      const currentTrack = get().currentTrack
      set({ remotePlayerId: player.player_id, remoteStatus: player, isPlaying: !!currentTrack })
      if (currentTrack) {
        remotePlayers.command(player.player_id, {
          kind: 'play',
          media_type: 'track',
          media_id: currentTrack.id,
          title: currentTrack.title,
        }).then(() => get().refreshRemoteStatus()).catch(console.error)
      }
    } else {
      localStorage.removeItem('s3nd-remote-player-id')
      set({ remotePlayerId: null, remoteStatus: null })
    }
  },

  refreshRemoteStatus: async () => {
    const id = get().remotePlayerId
    if (!id) return
    const players = await remotePlayers.list()
    const player = players.find((p) => p.player_id === id) || null
    if (!player || !player.status.online) {
      get().setRemotePlayer(null)
    } else {
      const status = player.status
      const duration = status.duration_seconds ?? get().duration
      const currentTime = status.position_seconds ?? get().currentTime
      const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : get().progress
      set({
        remoteStatus: player,
        isPlaying: !!status.playing && !status.paused,
        currentTime,
        duration,
        progress,
      })
    }
  },

  play: async (track) => {
    const { volume, remotePlayerId } = get()

    if (remotePlayerId) {
      await remotePlayers.command(remotePlayerId, {
        kind: 'play',
        media_type: 'track',
        media_id: track.id,
        title: track.title,
      })
      tracks.recordPlay(track.id).catch(() => {})
      set({
        currentTrack: track,
        isPlaying: true,
        progress: 0,
        duration: (track.duration_ms || 0) / 1000,
      })
      get().refreshRemoteStatus().catch(() => {})
      return
    }

    // Stop any video that might be playing
    useVideoPlayerStore.getState().stopVideo()

    // Cancel any existing animation frame
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    const audio = getAudioElement()

    // Stop current playback first
    audio.pause()
    audio.currentTime = 0

    // Remove old event listeners
    audio.onplay = null
    audio.onpause = null
    audio.onended = null
    audio.onerror = null
    audio.ontimeupdate = null

    // Set up new audio
    const streamUrl = tracks.streamUrl(track.id)
    audio.src = streamUrl
    audio.volume = volume
    audio.load() // Force reload

    // Set up event listeners
    audio.onplay = () => {
      set({ isPlaying: true })
    }

    audio.onpause = () => {
      set({ isPlaying: false })
    }

    audio.onended = () => {
      const { next } = get()
      next()
    }

    audio.onerror = (e) => {
      console.error('Failed to load track:', e)
      set({ isPlaying: false })
    }

    audio.ontimeupdate = () => {
      const currentStoredDuration = get().duration
      const audioDuration = audio.duration
      const audioDurationValid = isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0
      const effectiveDuration = audioDurationValid ? audioDuration : currentStoredDuration

      set({
        progress: effectiveDuration > 0 ? (audio.currentTime / effectiveDuration) * 100 : 0,
        duration: effectiveDuration,
        currentTime: audio.currentTime,
      })
    }

    // Start playback
    audio.play().catch((error) => {
      console.error('Playback failed:', error)
      set({ isPlaying: false })
    })

    // Record playback for recommendations (fire and forget)
    tracks.recordPlay(track.id).catch(() => {})

    set({
      currentTrack: track,
      progress: 0,
      duration: (track.duration_ms || 0) / 1000,
    })
  },

  playQueue: async (trackList, startIndex = 0) => {
    set({ queue: trackList, queueIndex: startIndex })

    // Find first non-downvoted track starting from startIndex
    let playIndex = startIndex
    while (playIndex < trackList.length) {
      const track = trackList[playIndex]
      const shouldSkip = await shouldSkipTrack(track.id)
      if (!shouldSkip) {
        set({ queueIndex: playIndex })
        await get().play(track)
        return
      }
      playIndex++
    }

    // If all tracks from startIndex are downvoted, check from beginning
    playIndex = 0
    while (playIndex < startIndex) {
      const track = trackList[playIndex]
      const shouldSkip = await shouldSkipTrack(track.id)
      if (!shouldSkip) {
        set({ queueIndex: playIndex })
        await get().play(track)
        return
      }
      playIndex++
    }

    // All tracks are downvoted - play nothing
    set({ isPlaying: false })
  },

  pause: () => {
    const { remotePlayerId, remoteStatus } = get()
    if (remotePlayerId) {
      set({ isPlaying: false, remoteStatus: remoteStatus ? { ...remoteStatus, status: { ...remoteStatus.status, paused: true } } : remoteStatus })
      remotePlayers.command(remotePlayerId, { kind: 'pause' }).then(() => get().refreshRemoteStatus()).catch(console.error)
      return
    }
    const audio = getAudioElement()
    audio.pause()
  },

  resume: () => {
    const { remotePlayerId, remoteStatus } = get()
    if (remotePlayerId) {
      set({ isPlaying: true, remoteStatus: remoteStatus ? { ...remoteStatus, status: { ...remoteStatus.status, paused: false } } : remoteStatus })
      remotePlayers.command(remotePlayerId, { kind: 'toggle_pause' }).then(() => get().refreshRemoteStatus()).catch(console.error)
      return
    }
    const audio = getAudioElement()
    audio.play().catch(console.error)
  },

  next: async () => {
    const { queue, queueIndex, play, repeatMode } = get()
    const audio = getAudioElement()

    // Handle repeat-one: restart current track
    if (repeatMode === 'one') {
      audio.currentTime = 0
      audio.play().catch(console.error)
      set({ progress: 0, currentTime: 0 })
      return
    }

    // Find next non-downvoted track
    let nextIndex = queueIndex + 1
    const startIndex = nextIndex // Track where we started to avoid infinite loops

    while (nextIndex < queue.length) {
      const nextTrack = queue[nextIndex]
      const shouldSkip = await shouldSkipTrack(nextTrack.id)
      if (!shouldSkip) {
        set({ queueIndex: nextIndex })
        await play(nextTrack)
        return
      }
      nextIndex++
    }

    // If we've checked all remaining tracks, try looping if repeat all is enabled
    if (repeatMode === 'all' && queue.length > 0) {
      nextIndex = 0
      while (nextIndex < startIndex && nextIndex < queue.length) {
        const nextTrack = queue[nextIndex]
        const shouldSkip = await shouldSkipTrack(nextTrack.id)
        if (!shouldSkip) {
          set({ queueIndex: nextIndex })
          await play(nextTrack)
          return
        }
        nextIndex++
      }
    }

    // No playable tracks found
    set({ isPlaying: false, progress: 0, currentTime: 0 })
  },

  previous: () => {
    const { queue, queueIndex, play, progress } = get()
    const audio = getAudioElement()

    // If we're more than 3 seconds in, restart the current track
    if (progress > 3) {
      audio.currentTime = 0
      set({ progress: 0, currentTime: 0 })
      return
    }
    if (queueIndex > 0) {
      const prevIndex = queueIndex - 1
      set({ queueIndex: prevIndex })
      play(queue[prevIndex])
    }
  },

  seek: (percent) => {
    const { duration: storedDuration, remotePlayerId } = get()
    if (remotePlayerId) {
      const seconds = storedDuration > 0 ? (percent / 100) * storedDuration : 0
      remotePlayers.command(remotePlayerId, { kind: 'seek', position_seconds: seconds }).then(() => get().refreshRemoteStatus()).catch(console.error)
      set({ progress: percent, currentTime: seconds })
      return
    }
    const audio = getAudioElement()
    const audioDuration = audio.duration
    // Use stored duration if audio duration is invalid (transcoded streams)
    const effectiveDuration = (isFinite(audioDuration) && audioDuration > 0) ? audioDuration : storedDuration
    if (effectiveDuration > 0) {
      audio.currentTime = (percent / 100) * effectiveDuration
      set({ progress: percent })
    }
  },

  setVolume: (volume) => {
    const { remotePlayerId } = get()
    if (remotePlayerId) {
      try {
        localStorage.setItem('s3nd-volume', volume.toString())
      } catch {}
      set({ volume, isMuted: false })
      return
    }
    const audio = getAudioElement()
    audio.volume = volume
    // Persist volume to localStorage
    try {
      localStorage.setItem('s3nd-volume', volume.toString())
    } catch {}
    set({ volume, isMuted: false })
  },

  toggleMute: () => {
    const { isMuted, volume, remotePlayerId } = get()
    if (remotePlayerId) {
      const nextMuted = !isMuted
      remotePlayers.command(remotePlayerId, { kind: 'volume', volume: nextMuted ? 0 : volume }).catch(console.error)
      set({ isMuted: nextMuted })
      return
    }
    const audio = getAudioElement()
    if (isMuted) {
      audio.volume = volume
      set({ isMuted: false })
    } else {
      audio.volume = 0
      set({ isMuted: true })
    }
  },

  addToQueue: (track) => {
    set((state) => ({ queue: [...state.queue, track] }))
  },

  removeFromQueue: (index) => {
    const { queue, queueIndex, currentTrack } = get()
    if (index < 0 || index >= queue.length) return

    const newQueue = [...queue]
    newQueue.splice(index, 1)

    // Adjust queueIndex if we removed a track before or at the current position
    let newQueueIndex = queueIndex
    if (index < queueIndex) {
      newQueueIndex = queueIndex - 1
    } else if (index === queueIndex && currentTrack) {
      // If we removed the currently playing track, don't change queueIndex
      // The current track will keep playing, and next() will play the new track at that index
    }

    set({ queue: newQueue, queueIndex: newQueueIndex })
  },

  reorderQueue: (fromIndex, toIndex) => {
    const { queue, queueIndex } = get()

    // Both indices are absolute queue indices (not relative to "Up Next")
    if (fromIndex < 0 || fromIndex >= queue.length || toIndex < 0 || toIndex >= queue.length) return
    if (fromIndex === toIndex) return

    // Don't allow moving the currently playing track or moving before it
    if (fromIndex <= queueIndex || toIndex <= queueIndex) return

    const newQueue = [...queue]
    const [movedTrack] = newQueue.splice(fromIndex, 1)
    newQueue.splice(toIndex, 0, movedTrack)

    set({ queue: newQueue })
  },

  playFromQueue: (index) => {
    const { queue, play } = get()
    if (index >= 0 && index < queue.length) {
      set({ queueIndex: index })
      play(queue[index])
    }
  },

  clearQueue: () => {
    set({ queue: [], queueIndex: 0, originalQueue: [] })
  },

  toggleShuffle: () => {
    const { shuffleEnabled, queue, queueIndex } = get()

    if (!shuffleEnabled) {
      // Enable shuffle: save original queue and shuffle remaining tracks
      const originalQueue = [...queue]

      // Get tracks after current position
      const beforeCurrent = queue.slice(0, queueIndex + 1)
      const afterCurrent = queue.slice(queueIndex + 1)

      // Fisher-Yates shuffle for remaining tracks
      const shuffled = [...afterCurrent]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }

      set({
        shuffleEnabled: true,
        originalQueue,
        queue: [...beforeCurrent, ...shuffled],
      })
    } else {
      // Disable shuffle: restore original queue
      const { originalQueue, currentTrack } = get()
      if (originalQueue.length > 0 && currentTrack) {
        // Find current track position in original queue
        const newIndex = originalQueue.findIndex((t) => t.id === currentTrack.id)
        set({
          shuffleEnabled: false,
          queue: originalQueue,
          queueIndex: newIndex >= 0 ? newIndex : 0,
          originalQueue: [],
        })
      } else {
        set({ shuffleEnabled: false, originalQueue: [] })
      }
    }
  },

  cycleRepeatMode: () => {
    const { repeatMode } = get()
    const modes: ('off' | 'one' | 'all')[] = ['off', 'all', 'one']
    const currentIndex = modes.indexOf(repeatMode)
    const nextMode = modes[(currentIndex + 1) % modes.length]
    set({ repeatMode: nextMode })
  },

  restoreState: async () => {
    try {
      isRestoring = true
      const savedState = await playerState.get()

      if (!savedState || !savedState.queue || savedState.queue.length === 0) {
        set({ stateLoaded: true })
        isRestoring = false
        return
      }

      // Fetch track details for all tracks in the queue
      const trackDetails = await Promise.all(
        savedState.queue.map(async (trackId) => {
          try {
            return await tracks.get(trackId)
          } catch {
            return null
          }
        })
      )

      // Filter out any tracks that couldn't be fetched
      const validTracks = trackDetails.filter((t): t is TrackWithDetails => t !== null)

      if (validTracks.length === 0) {
        set({ stateLoaded: true })
        isRestoring = false
        return
      }

      // Find the current track
      const currentTrackIndex = Math.min(savedState.queue_index, validTracks.length - 1)
      const currentTrack = validTracks[currentTrackIndex]

      // Restore state
      set({
        queue: validTracks,
        queueIndex: currentTrackIndex,
        currentTrack,
        shuffleEnabled: savedState.shuffle_enabled,
        repeatMode: savedState.repeat_mode,
        duration: (currentTrack.duration_ms || 0) / 1000,
        stateLoaded: true,
      })

      // Set up audio for the current track without auto-playing
      const audio = getAudioElement()
      audio.src = tracks.streamUrl(currentTrack.id)
      audio.volume = get().volume

      // Seek to saved position
      const positionSec = savedState.position_ms / 1000
      audio.currentTime = positionSec

      // Set up event listeners
      audio.onplay = () => set({ isPlaying: true })
      audio.onpause = () => set({ isPlaying: false })
      audio.onended = () => get().next()
      audio.onerror = (e) => {
        console.error('Failed to load track:', e)
        set({ isPlaying: false })
      }
      audio.ontimeupdate = () => {
        const currentStoredDuration = get().duration
        const audioDuration = audio.duration
        const audioDurationValid = isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0
        const effectiveDuration = audioDurationValid ? audioDuration : currentStoredDuration
        set({
          progress: effectiveDuration > 0 ? (audio.currentTime / effectiveDuration) * 100 : 0,
          duration: effectiveDuration,
          currentTime: audio.currentTime,
        })
      }

      console.log('Player state restored:', {
        queueLength: validTracks.length,
        currentTrack: currentTrack.title,
        position: positionSec
      })
    } catch (error) {
      console.error('Failed to restore player state:', error)
      set({ stateLoaded: true })
    } finally {
      isRestoring = false
    }
  },
}))

// Save state to server (debounced)
function saveStateToServer() {
  if (isRestoring) return

  if (saveStateTimer) {
    clearTimeout(saveStateTimer)
  }

  saveStateTimer = setTimeout(async () => {
    const state = usePlayerStore.getState()

    // Don't save if no queue
    if (state.queue.length === 0) return

    const audio = getAudioElement()
    const stateData: PlayerStateData = {
      current_track_id: state.currentTrack?.id || null,
      queue_index: state.queueIndex,
      position_ms: Math.floor(audio.currentTime * 1000),
      shuffle_enabled: state.shuffleEnabled,
      repeat_mode: state.repeatMode,
      queue: state.queue.map(t => t.id),
    }

    try {
      await playerState.save(stateData)
    } catch (error) {
      console.error('Failed to save player state:', error)
    }
  }, 2000) // 2 second debounce
}

// Subscribe to state changes and save
usePlayerStore.subscribe((state, prevState) => {
  // Only save if relevant state changed
  if (
    state.currentTrack?.id !== prevState.currentTrack?.id ||
    state.queueIndex !== prevState.queueIndex ||
    state.queue.length !== prevState.queue.length ||
    state.shuffleEnabled !== prevState.shuffleEnabled ||
    state.repeatMode !== prevState.repeatMode
  ) {
    saveStateToServer()
  }
})

// Also save periodically while playing to update position
setInterval(() => {
  const state = usePlayerStore.getState()
  if (state.isPlaying && state.currentTrack) {
    saveStateToServer()
  }
}, 30000) // Every 30 seconds while playing
